import { downloadZip } from "client-zip";
import {
  ExportMediaUrlDto,
  ExportMediaUrlsDto,
  fetchExportMediaUrls,
} from "../../api/exportsApi";
import { safeEntryName } from "./exportMediaPaths";

/**
 * Fetch an export's photos in the browser and hand back a single zip (AKL-554).
 *
 * The bytes go blob storage -> browser directly; they never pass through the akili API.
 * That is a hard requirement (data sovereignty), and it is also what keeps the server off
 * the critical path for something that can be gigabytes.
 *
 * `client-zip` is used rather than the File System Access API because that API is
 * Chromium-only and Firefox has stated it will not implement it — the web app must work
 * in any browser. The tradeoff is that browsers without a streaming save target buffer
 * the zip in memory, which is why the server gates this path behind a conservative size
 * threshold (`browser_download_supported` on the estimate) and routes anything larger to
 * the desktop transfer tool.
 *
 * A download can outlast a network gap (AKL-892). Before every retry a worker waits for
 * the browser to report itself online, then backs off (1 s, 3 s, 9 s, 27 s, then 30 s);
 * each request is abandoned after 60 s without a byte; and a 401/403 on a URL whose page
 * has expired re-fetches that one page of URLs once and retries with the fresh one. The
 * zip only ever holds complete images — a body that was cut off is dropped, not written.
 */

/** Parallel image fetches. Matches AzureUploadManager's concurrency for the same reason:
 *  enough to saturate a link, few enough not to trip storage-side throttling. */
const CONCURRENCY = 5;

/**
 * Real requests per image before it is recorded as failed. Waiting for the browser to
 * come back online is not an attempt. Eight, because the back-off between them adds up
 * to about 130 s (see `backoffDelayMs`): a two-minute gap the browser never reports —
 * Wi-Fi up, no route — is outlasted, not only a gap it does report.
 */
const MAX_ATTEMPTS = 8;

/**
 * A request that goes this long without delivering a byte is abandoned and retried.
 * Measured from the last byte, not from the start: a slow link moving an 8 MB image
 * legitimately takes longer than a minute, and cutting it off would fail exactly the
 * users this exists for. A hung socket after a sleep delivers nothing at all.
 *
 * Worst case, a link that accepts every connection and never sends a byte holds one
 * image for MAX_ATTEMPTS deadlines plus the back-off — about ten minutes — before it
 * is recorded failed. Deliberate: that link is not coming back on its own, the
 * progress line says what is happening, and Cancel stops it at once.
 */
const STALL_TIMEOUT_MS = 60_000;

/** Back-off between attempts: BASE × FACTOR^(n-1), never more than CAP. */
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_FACTOR = 3;
const BACKOFF_CAP_MS = 30_000;

/**
 * A 401/403 this close to (or past) the page's `expires_at` is treated as an expired
 * URL rather than a revoked permission. Covers clock drift between the browser and
 * the storage account, and a URL that expires while its request is in flight.
 */
const URL_EXPIRY_GRACE_MS = 60_000;

export type MediaDownloadProgress = {
  /** Images written into the zip so far. */
  done: number;
  /** Images discovered so far. Grows while paging, so it is a floor until `paging` is false. */
  total: number;
  /** True while still walking the URL pages, i.e. `total` is not final yet. */
  paging: boolean;
  /** Images that could not be fetched after MAX_ATTEMPTS. */
  failed: number;
  /** True while at least one worker is parked waiting for the browser to come back online. */
  waitingForNetwork: boolean;
};

export type MediaDownloadResult = {
  fileName: string;
  blob: Blob;
  downloaded: number;
  failed: ExportMediaUrlDto[];
};

export class MediaDownloadCancelledError extends Error {
  constructor() {
    super("Image download cancelled");
    this.name = "MediaDownloadCancelledError";
  }
}

/**
 * How long to wait before the next attempt once `failedAttempts` have failed:
 * 1 s, 3 s, 9 s, 27 s, then 30 s a time. Exported so the schedule can be asserted on
 * directly rather than inferred from timings.
 * @param failedAttempts attempts made so far, at least 1
 * @returns the delay in milliseconds
 */
export const backoffDelayMs = (failedAttempts: number): number =>
  Math.min(
    BACKOFF_BASE_MS * BACKOFF_FACTOR ** (failedAttempts - 1),
    BACKOFF_CAP_MS,
  );

/**
 * One image to fetch, plus what it takes to re-mint its URL if that URL expires: the
 * page it came from and when the API said that page's URLs stop working.
 */
type QueuedMedia = {
  /** The row as the API listed it; this is what a failure is reported against. */
  item: ExportMediaUrlDto;
  /** The URL to fetch — `item.url` until a re-mint replaces it. */
  url: string;
  /** The cursor that fetched this image's page; undefined for the first page. */
  cursor: string | undefined;
  /** The page's `expires_at` as epoch milliseconds, or null when absent or unreadable. */
  expiresAt: number | null;
};

/** What a worker needs from the download as a whole while it fetches one image. */
type FetchImageHooks = {
  /** Called with true when the worker parks on the `online` event, false when it wakes. */
  onWaitingForNetwork: (waiting: boolean) => void;
  /**
   * Re-mint the image's URL from a fresh copy of its page. Resolves to the new URL, or
   * null when the page cannot be fetched or no longer lists the image.
   */
  refreshUrl: (queued: QueuedMedia) => Promise<string | null>;
};

/** Parse an API timestamp to epoch milliseconds; null when absent or unparsable. */
const parseExpiry = (value: string | null): number | null => {
  if (value === null) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
};

/** Whether a known expiry is past, or within the grace window of being past. */
const isPastExpiry = (expiresAt: number): boolean =>
  Date.now() >= expiresAt - URL_EXPIRY_GRACE_MS;

/**
 * Whether a 401/403 on this image is best explained by its URL having expired. An
 * unknown expiry counts as expired: the alternative is a certain failure, and the cost
 * of being wrong is one page request shared across that page's images.
 */
const urlLooksExpired = (queued: QueuedMedia): boolean =>
  queued.expiresAt === null || isPastExpiry(queued.expiresAt);

/**
 * Walk every page of an export's media URLs.
 *
 * Sequential by necessity, not choice: each page's cursor comes from the previous
 * response, so there is nothing to parallelise. Pages are small and this runs alongside
 * the image fetches rather than blocking them.
 * @param exportGuid the export job guid
 * @param signal abort signal checked between pages
 * @param onPage called with each page as it arrives, and the cursor that fetched it
 */
async function walkMediaUrls(
  exportGuid: string,
  signal: AbortSignal,
  onPage: (page: ExportMediaUrlsDto, cursor: string | undefined) => void,
): Promise<void> {
  let cursor: string | undefined;
  do {
    if (signal.aborted) throw new MediaDownloadCancelledError();
    const page = await fetchExportMediaUrls(exportGuid, cursor);
    // Re-check after the await: a cancel landing while the request was in flight
    // would otherwise queue another page of work and report progress for a panel
    // the user has already closed.
    if (signal.aborted) throw new MediaDownloadCancelledError();
    onPage(page, cursor);
    cursor = page.next_cursor ?? undefined;
  } while (cursor);
}

/**
 * Whether the browser reports a network. A coarse signal — true only says a network
 * interface is up — but false is reliable, and it is what stops five workers burning
 * through every attempt while the laptop is still waking its Wi-Fi.
 */
const browserIsOnline = (): boolean =>
  typeof navigator === "undefined" || navigator.onLine;

/**
 * Resolve once the browser reports itself online, or at once if it already does.
 * @param signal cancel signal; rejects with MediaDownloadCancelledError if it fires first
 */
function whenOnline(signal: AbortSignal): Promise<void> {
  if (browserIsOnline()) return Promise.resolve();
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new MediaDownloadCancelledError());
      return;
    }
    const cleanup = () => {
      window.removeEventListener("online", onOnline);
      signal.removeEventListener("abort", onAbort);
    };
    const onOnline = () => {
      cleanup();
      resolve();
    };
    const onAbort = () => {
      cleanup();
      reject(new MediaDownloadCancelledError());
    };
    window.addEventListener("online", onOnline);
    signal.addEventListener("abort", onAbort);
  });
}

/**
 * A sleep that a cancel cuts short.
 * @param ms how long to sleep
 * @param signal cancel signal; rejects with MediaDownloadCancelledError if it fires first
 */
function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new MediaDownloadCancelledError());
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new MediaDownloadCancelledError());
    };
    signal.addEventListener("abort", onAbort);
  });
}

/**
 * Settle with `promise`, unless the cancel signal fires first — then reject with the
 * cancel error and leave the original to finish on its own. For work that cannot be
 * aborted (the API client's own request) but must not keep the download alive after
 * the panel has closed.
 * @param promise the work to wait for
 * @param signal cancel signal
 * @returns whatever `promise` resolves to
 */
function raceWithCancel<T>(
  promise: Promise<T>,
  signal: AbortSignal,
): Promise<T> {
  if (signal.aborted) return Promise.reject(new MediaDownloadCancelledError());
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(new MediaDownloadCancelledError());
    signal.addEventListener("abort", onAbort);
    promise.then(resolve, reject).finally(() => {
      signal.removeEventListener("abort", onAbort);
    });
  });
}

/**
 * Collect a response body into a Blob, reporting each chunk as it lands so the caller
 * can tell a slow download from a stalled one. Where the body is not exposed as a stream
 * it falls back to `blob()`, and the whole request then shares one deadline.
 * @param response a successful response
 * @param onChunk called every time part of the body arrives
 * @returns the complete body
 */
async function readBody(
  response: Response,
  onChunk: () => void,
): Promise<Blob> {
  const body = response.body;
  if (!body) return response.blob();
  const reader = body.getReader();
  const chunks: BlobPart[] = [];
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      onChunk();
    }
  }
  return new Blob(chunks);
}

type AttemptOutcome =
  | { kind: "ok"; blob: Blob }
  /** A 4xx: the server answered and the answer will not change on its own. */
  | { kind: "rejected"; status: number }
  /** No usable answer — a network failure, a stalled request, or a 5xx. Worth retrying. */
  | { kind: "transient" }
  | { kind: "cancelled" };

/**
 * One request for one image, with its own deadline.
 *
 * The request gets a private AbortController chained to the cancel signal, so the
 * inactivity timer can abandon this attempt without touching the download's own signal.
 * The timer restarts whenever a byte arrives.
 * @param url the signed image URL
 * @param signal the download's cancel signal
 * @returns what happened, classified for the retry loop
 */
async function attemptFetch(
  url: string,
  signal: AbortSignal,
): Promise<AttemptOutcome> {
  const attempt = new AbortController();
  const abandon = () => attempt.abort();
  signal.addEventListener("abort", abandon);
  if (signal.aborted) abandon();
  let stall = setTimeout(abandon, STALL_TIMEOUT_MS);
  const touch = () => {
    clearTimeout(stall);
    stall = setTimeout(abandon, STALL_TIMEOUT_MS);
  };
  try {
    const response = await fetch(url, { signal: attempt.signal });
    if (!response.ok) {
      return response.status >= 400 && response.status < 500
        ? { kind: "rejected", status: response.status }
        : { kind: "transient" };
    }
    // The timer is deliberately not restarted when the headers arrive: the deadline is
    // 60 s without a byte of the image, so a response that takes 59 s to answer and
    // then stalls is cut at 60 s, not at 119 s.
    return { kind: "ok", blob: await readBody(response, touch) };
  } catch {
    // A fetch that threw: the network, the deadline, or the user. Only the last is not
    // worth another try.
    return signal.aborted ? { kind: "cancelled" } : { kind: "transient" };
  } finally {
    clearTimeout(stall);
    signal.removeEventListener("abort", abandon);
  }
}

/**
 * Fetch one image, riding out transient failures.
 *
 * A single unreadable blob must not sink an otherwise good download — the caller records
 * it and carries on, exactly as the server-side builder used to. Between attempts the
 * worker waits for the browser to be online, then backs off 1 s, 3 s, 9 s, 27 s and
 * then 30 s a time; each attempt is abandoned after 60 s without a byte. A 401/403 on a
 * URL whose page has expired is re-minted once through `hooks.refreshUrl` and retried
 * straight away — that attempt is refunded, since an expired URL says nothing about the
 * network. Every other 4xx is final on the first answer.
 * @param queued the image and the page it came from
 * @param signal cancel signal
 * @param hooks the download-wide services a worker needs
 * @returns the bytes, or null if every attempt failed
 */
async function fetchImage(
  queued: QueuedMedia,
  signal: AbortSignal,
  hooks: FetchImageHooks,
): Promise<Blob | null> {
  let attempts = 0;
  let reminted = false;
  let waitBeforeNext = false;
  for (;;) {
    if (signal.aborted) throw new MediaDownloadCancelledError();
    if (attempts >= MAX_ATTEMPTS) return null;
    if (waitBeforeNext) {
      if (!browserIsOnline()) {
        hooks.onWaitingForNetwork(true);
        try {
          await whenOnline(signal);
        } finally {
          hooks.onWaitingForNetwork(false);
        }
      }
      await delay(backoffDelayMs(attempts), signal);
    }
    attempts += 1;
    const outcome = await attemptFetch(queued.url, signal);
    if (outcome.kind === "ok") return outcome.blob;
    if (outcome.kind === "cancelled") throw new MediaDownloadCancelledError();
    if (outcome.kind === "rejected") {
      const denied = outcome.status === 401 || outcome.status === 403;
      if (denied && !reminted && urlLooksExpired(queued)) {
        // Raced against the cancel signal: the page request cannot itself be aborted,
        // and a closed panel must not stay alive until the API client times out.
        const fresh = await raceWithCancel(hooks.refreshUrl(queued), signal);
        if (signal.aborted) throw new MediaDownloadCancelledError();
        if (fresh === null) return null;
        queued.url = fresh;
        reminted = true;
        attempts -= 1;
        waitBeforeNext = false;
        continue;
      }
      // Anything else a 4xx says will not change on retry: an image that is gone, a
      // permission that was revoked, a URL that was rejected for a second time.
      return null;
    }
    waitBeforeNext = true;
  }
}

/**
 * Download an export's photos as one zip, laid out to match its data package.
 *
 * Entries are written at `relative_path`, which the API guarantees is byte-identical to
 * `media.filePath` in media.csv. Unzipping this alongside the data package therefore
 * produces a self-contained CamTrap DP folder that `read_camtrapdp()` can resolve.
 *
 * Paging and fetching are interleaved: images from page 1 download while page 2 is being
 * requested, so a large export does not sit idle waiting for the full URL list.
 *
 * Each queued image keeps its page's cursor and expiry, so an expired URL can be
 * re-minted by fetching that page again. A page is re-fetched once however many workers
 * need it, and again only if that fresh copy has itself expired.
 *
 * @param exportGuid the export job guid
 * @param fileName name for the resulting zip
 * @param onProgress called as images complete, fail, or wait for the network
 * @param signal abort signal; cancelling rejects with MediaDownloadCancelledError
 * @returns the assembled zip plus a list of anything that could not be fetched
 */
export async function downloadExportMedia(
  exportGuid: string,
  fileName: string,
  onProgress: (progress: MediaDownloadProgress) => void,
  signal: AbortSignal,
): Promise<MediaDownloadResult> {
  const queue: QueuedMedia[] = [];
  const failed: ExportMediaUrlDto[] = [];
  const entries: { name: string; input: Blob }[] = [];
  let paging = true;
  let discovered = 0;
  let waitingWorkers = 0;

  // The workers' own cancel signal: fires with the caller's, and also when paging fails
  // — Promise.all rejects on the first failure but does not stop the other promises,
  // and a worker mid-retry would otherwise keep fetching, and keep reporting progress
  // to a panel that has already discarded this download, for minutes.
  const workers = new AbortController();
  const stopWorkers = () => workers.abort();
  signal.addEventListener("abort", stopWorkers);
  if (signal.aborted) stopWorkers();

  const report = () => {
    // Nothing after the download is over: a late report would resurrect the panel's
    // progress line for a download it no longer owns.
    if (workers.signal.aborted) return;
    onProgress({
      done: entries.length,
      total: discovered,
      paging,
      failed: failed.length,
      waitingForNetwork: waitingWorkers > 0,
    });
  };

  // Fresh copies of pages whose URLs expired, keyed by cursor. `expiresAt` is undefined
  // while the request is in flight, so workers arriving meanwhile share it.
  type RefreshedPage = {
    page: Promise<ExportMediaUrlsDto>;
    expiresAt: number | null | undefined;
  };
  const refreshedPages = new Map<string, RefreshedPage>();

  const refreshPage = (
    cursor: string | undefined,
  ): Promise<ExportMediaUrlsDto> => {
    const key = cursor ?? "";
    const cached = refreshedPages.get(key);
    // Reuse a request still in flight, or a resolved page known to be current. A
    // resolved page with no readable expiry is not reused: there is no way to tell
    // whether its URLs still work, and handing out a stale one would spend the
    // image's only re-mint on a URL that is about to fail again.
    if (
      cached &&
      (cached.expiresAt === undefined ||
        (typeof cached.expiresAt === "number" &&
          !isPastExpiry(cached.expiresAt)))
    ) {
      return cached.page;
    }
    // Not the default 5xx handling: that hard-redirects the whole page, which would
    // throw away every image already fetched over one bad answer. The rejection is
    // caught in `refreshUrl` and costs this image alone.
    const entry: RefreshedPage = {
      page: fetchExportMediaUrls(exportGuid, cursor, {
        suppressUnexpectedErrorRedirect: true,
      }),
      expiresAt: undefined,
    };
    entry.page.then(
      (page) => {
        entry.expiresAt = parseExpiry(page.expires_at);
      },
      () => {
        // Do not cache a failure: the next image on this page gets a fresh try.
        if (refreshedPages.get(key) === entry) refreshedPages.delete(key);
      },
    );
    refreshedPages.set(key, entry);
    return entry.page;
  };

  const hooks: FetchImageHooks = {
    onWaitingForNetwork: (waiting) => {
      waitingWorkers += waiting ? 1 : -1;
      report();
    },
    refreshUrl: async (queued) => {
      try {
        const page = await refreshPage(queued.cursor);
        const fresh = page.items.find(
          (candidate) => candidate.media_guid === queued.item.media_guid,
        );
        if (fresh === undefined) return null;
        queued.expiresAt = parseExpiry(page.expires_at);
        return fresh.url;
      } catch {
        // Already logged by the API client. The image is recorded failed and the
        // download carries on: rejecting here would discard every image already fetched.
        return null;
      }
    },
  };

  // Set when paging fails so an idle worker stops too; a busy one is stopped through
  // `workers` above. The paging error is kept so it, not the cancellation it causes in
  // the workers, is what the caller sees — whichever rejection reaches Promise.all first.
  let pagingFailed = false;
  let pagingError: unknown;

  const paging$ = walkMediaUrls(exportGuid, signal, (page, cursor) => {
    const expiresAt = parseExpiry(page.expires_at);
    queue.push(
      ...page.items.map((item) => ({ item, url: item.url, cursor, expiresAt })),
    );
    discovered += page.items.length;
    report();
  })
    .catch((error: unknown) => {
      pagingFailed = true;
      pagingError = error;
      stopWorkers();
      throw error;
    })
    .finally(() => {
      paging = false;
    });

  /**
   * One worker: pull from the shared queue until it is empty and paging has finished.
   * Waiting on `paging` rather than exiting on an empty queue is what lets fetching start
   * before the full URL list is known.
   */
  const worker = async (): Promise<void> => {
    for (;;) {
      if (pagingFailed) return;
      // An idle worker must notice a cancel too: with the queue empty and the first page
      // of URLs still pending, nothing else would, and the download would stay alive
      // until that non-abortable request settled. After `pagingFailed`, so a paging
      // error remains what the caller sees.
      if (workers.signal.aborted) throw new MediaDownloadCancelledError();
      // `pop()` rather than `shift()`: shift re-indexes the whole array on every call.
      // Order is irrelevant here because entries are sorted by name before zipping.
      const queued = queue.pop();
      if (queued === undefined) {
        if (!paging) return;
        // Nothing queued yet but more pages are coming. Yield briefly rather than spin.
        await new Promise((resolve) => setTimeout(resolve, 50));
        continue;
      }
      // Validate before fetching: an untrustworthy path means the bytes have
      // nowhere safe to go, so spending the request would be wasted.
      const name = safeEntryName(queued.item.relative_path);
      if (name === null) {
        failed.push(queued.item);
        report();
        continue;
      }
      const blob = await fetchImage(queued, workers.signal, hooks);
      if (blob === null) {
        failed.push(queued.item);
      } else {
        entries.push({ name, input: blob });
      }
      report();
    }
  };

  try {
    // Surface a paging failure (an expired session, a revoked permission) rather than
    // letting the workers drain a queue that will never grow and report a silent success.
    try {
      await Promise.all([
        paging$,
        ...Array.from({ length: CONCURRENCY }, worker),
      ]);
    } catch (error: unknown) {
      // The caller's cancel is the truthful outcome whatever else broke on the way
      // out. The page walk is not given the caller's signal, so a cancel does not stop
      // a page request already in flight; if that request then rejects, its error would
      // otherwise be thrown in place of the cancellation a worker had already raised.
      // Not a rare coincidence either: the commonest reason to press Cancel is a
      // download that looks stuck, for the same lost network that is about to fail the
      // request. Nothing is lost by preferring the cancel — the caller has discarded
      // this download and shows neither outcome.
      if (signal.aborted) throw new MediaDownloadCancelledError();
      throw pagingFailed ? pagingError : error;
    }

    if (signal.aborted) throw new MediaDownloadCancelledError();

    // Deterministic order so two runs of the same export produce comparable archives;
    // entries land in completion order otherwise, which varies with network timing.
    entries.sort((a, b) => a.name.localeCompare(b.name));

    const blob = await downloadZip(entries).blob();
    // Assembling a large zip takes real time, and a cancel that lands during it must
    // not hand the caller a file to save under a panel the user has already closed.
    if (signal.aborted) throw new MediaDownloadCancelledError();
    return { fileName, blob, downloaded: entries.length, failed };
  } finally {
    signal.removeEventListener("abort", stopWorkers);
  }
}

/**
 * Hand a blob to the browser as a file download.
 *
 * The object URL is revoked on the next tick rather than immediately: revoking before the
 * click has been processed cancels the download in some browsers.
 * @param blob the file contents
 * @param fileName the name to save as
 */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
